/**
 * HVP Cascade Controller - variation resolution
 *
 * Resolves a complete selection to a variation and owns the authoritative
 * server re-check on the no-match dead end (stale cached client blob).
 *
 * Split out of cascade-controller.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPCascadeController.prototype by
 * cascade-controller.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

import { showToast } from './toast.js';

export const CascadeVariationMethods = {
    /**
     * Find matching variation
     */
    async findMatchingVariation() {
        const start = performance.now();
        this.setFormLoading(true);

        try {
            let variation = null;

            // USE CLIENT-SIDE CASCADE IF AVAILABLE (instant, no AJAX)
            if (this.clientCascade && this.clientCascade.isAvailable()) {
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] CLIENT-SIDE MATCH - Finding variation locally');
                variation = this.clientCascade.findVariation(this.selections);

                if (variation) {
                    const elapsed = Math.round(performance.now() - start);
                    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] CLIENT-SIDE found variation', variation.variation_id, 'in', elapsed, 'ms');
                }
            } else {
                // FALLBACK: Server-side AJAX (slow)
                window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] SERVER-SIDE MATCH - Using AJAX (slow fallback)');
                const result = await this.api.match(this.productId, this.selections);
                variation = result?.variation;
            }

            if (variation && variation.variation_id) {
                this.currentVariation = variation;
                this.applyVariation(variation);

                this.log('Variation found:', variation.variation_id);

                this.emit('variationFound', {
                    variation: variation
                });

                this.fireCustomEvent('hvp_variation_found', {
                    variation: variation
                });

            } else {
                this.currentVariation = null;
                this.clearVariation();

                this.log('No matching variation');

                this.emit('noVariation', {
                    selections: { ...this.selections }
                });

                // Surface the dead-end to the DOM. WooCommerce's native
                // "no products matched your selection" message is suppressed for
                // HVP-controlled forms (wc-override.js), and clearVariation()
                // above leaves the form visually identical to the "nothing
                // selected yet" state. Without a DOM event nothing downstream
                // (the no-match notice, RPS, coachmarks) can react and the user
                // is left at a silent dead-end. Guard on completeness so this
                // only fires when every attribute is chosen and STILL resolves
                // to no variation — never mid-cascade.
                if (this.areAllSelectionsComplete()) {
                    await this.recheckNoMatch();
                }
            }

        } catch (error) {
            console.error('[HVP] Match variation error:', error);
            this.clearVariation();

        } finally {
            this.setFormLoading(false);
        }
    },

    /**
     * Authoritative no-match re-check (the dead-end path ONLY).
     *
     * findMatchingVariation() resolves client-side from the page-baked
     * allVariations blob, which for anonymous visitors can be a stale
     * FlyingPress-cached copy (admins bypass the cache). Before committing to a
     * "no such combination" dead-end, ask the server exactly once:
     *   found:true  -> the client blob was stale; apply the server's variation,
     *                  fire hvp_variation_found, toast "Options refreshed".
     *   found:false -> genuine no-combination; fire hvp_no_match (no_combination).
     *   throw       -> 403 (stale nonce) / network / timeout; we could not confirm,
     *                  so fire hvp_no_match (transport), not a false dead-end.
     * No auto-reload here - the modal owns the reload CTA (user decision).
     */
    async recheckNoMatch() {
        const attemptedAttributes = this.attributeOrder.map(
            ( attr ) => this.normalizeAttributeName( attr )
        );

        let result;
        try {
            result = await this.api.findVariation( this.productId, this.selections );
        } catch ( error ) {
            const info = this.api.handleError( error );
            console.warn( '[HVP] No-match re-check failed (' + info.type + '):', info.message );
            this.fireCustomEvent( 'hvp_no_match', {
                selections: { ...this.selections },
                attemptedAttributes,
                reason: 'transport',
                transportType: info.type
            } );
            return;
        }

        if ( result && result.found && result.variation && result.variation.variation_id ) {
            // Stale client dataset - the server has this combination. Recover silently.
            this.currentVariation = result.variation;
            this.applyVariation( result.variation );
            this.log( 'No-match re-check recovered variation:', result.variation.variation_id );

            this.emit( 'variationFound', { variation: result.variation } );
            this.fireCustomEvent( 'hvp_variation_found', { variation: result.variation } );
            this.fireCustomEvent( 'hvp_options_refreshed', {
                variationId: result.variation.variation_id,
                dataVersion: result.data_version || null
            } );

            const msg = ( window.hvpData && window.hvpData.i18n && window.hvpData.i18n.optionsRefreshed )
                || 'Options refreshed';
            showToast( msg );
            return;
        }

        // Server confirms: no published variation matches this complete selection.
        this.fireCustomEvent( 'hvp_no_match', {
            selections: { ...this.selections },
            attemptedAttributes,
            reason: 'no_combination',
            dataVersion: ( result && result.data_version ) || null
        } );
    },
};

export default CascadeVariationMethods;
